import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'

export default function MfaVerify() {
  const { verifyMfa, verifyMfaBackup, cancelMfa } = useAuth()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [useBackup, setUseBackup] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [useBackup])

  const handleSubmit = async (e) => {
    e?.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = useBackup
        ? await verifyMfaBackup(code)
        : await verifyMfa(code)

      if (!result.success) {
        setError(result.error || 'Invalid code')
        setCode('')
        inputRef.current?.focus()
      }
    } catch (err) {
      setError('Verification failed')
      setCode('')
      inputRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (e) => {
    const val = useBackup ? e.target.value : e.target.value.replace(/\D/g, '')
    setCode(val)

    // Auto-submit on 6 digits for TOTP
    if (!useBackup && val.length === 6) {
      setTimeout(() => handleSubmitDirect(val), 0)
    }
  }

  const handleSubmitDirect = async (directCode) => {
    setError('')
    setLoading(true)
    try {
      const result = await verifyMfa(directCode)
      if (!result.success) {
        setError(result.error || 'Invalid code')
        setCode('')
        inputRef.current?.focus()
      }
    } catch (err) {
      setError('Verification failed')
      setCode('')
      inputRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center safe-screen">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/images/scenelight-logo.png" alt="Scenelight" className="w-28 h-28 mx-auto" />
          <h1 className="text-4xl text-white mt-4 tracking-widest" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>SCENELIGHT</h1>
          <p className="text-slate-400 mt-2">
            {useBackup
              ? 'Enter one of your backup codes'
              : 'Enter the 6-digit code from your authenticator app'}
          </p>
        </div>

        <div className="card">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                {useBackup ? 'Backup Code' : 'Authentication Code'}
              </label>
              <input
                ref={inputRef}
                type="text"
                value={code}
                onChange={handleCodeChange}
                maxLength={useBackup ? 9 : 6}
                placeholder={useBackup ? 'xxxx-xxxx' : '000000'}
                autoComplete="one-time-code"
                autoFocus
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-center text-xl font-mono tracking-widest placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading || (!useBackup && code.length !== 6) || (useBackup && code.length < 4)}
              className="w-full btn btn-primary py-3 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => { setUseBackup(!useBackup); setCode(''); setError('') }}
              className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
            >
              {useBackup ? 'Use authenticator app' : 'Use a backup code'}
            </button>
            <button
              type="button"
              onClick={cancelMfa}
              className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
